import BusinessIcon from "@mui/icons-material/Business";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAccount, useToast } from "../../../context";
import authService from "../../../services/auth";
import Header from "../../chat/Header/Header";
import { CardSkeleton } from "../../shared/SkeletonLoader";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 64px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ImageContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  background-color: #e0f2f1;
  color: #008069;
  font-size: 50px;
  border: 3px solid #008069;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 20px;
  margin-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 13px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled(Box)`
  color: #4a4a4a;
  font-size: 14px;
  margin: 3px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const InfoLabel = styled(Typography)`
  color: #4a4a4a;
  font-size: 14px;
`;

const fallback = (value?: string, fallbackText = "Não especificado") =>
  value || fallbackText;

const UserProfile = () => {
  const { account } = useAccount();
  const { showToast } = useToast();
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  if (showSkeleton) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Perfil do Usuário" />
        </Box>
        <ContentContainer>
          <ImageContainer>
            <Skeleton variant="circular" width={100} height={100} />
          </ImageContainer>

          {/* Informações Pessoais */}
          <BoxWrapper>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width="50%" height={18} />
            </Box>
            <Skeleton variant="text" width="70%" height={22} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="45%" height={18} />
          </BoxWrapper>

          {/* Contato */}
          <BoxWrapper>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width="35%" height={18} />
            </Box>
            <Skeleton variant="text" width="55%" height={22} />
          </BoxWrapper>

          {/* Empresa */}
          <BoxWrapper>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Skeleton variant="circular" width={18} height={18} />
              <Skeleton variant="text" width="35%" height={18} />
            </Box>
            <Skeleton variant="text" width="65%" height={22} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="50%" height={18} />
          </BoxWrapper>
        </ContentContainer>

        {/* Rodapé (botão Sair) */}
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
            minHeight: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Skeleton
            variant="rounded"
            width="100%"
            height={40}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Perfil do Usuário" />
        </Box>
        <ContentContainer>
          <Box sx={{ p: 2 }}>
            <CardSkeleton showAvatar={true} lines={5} />
          </Box>
        </ContentContainer>
      </Container>
    );
  }

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);

    try {
      await authService.logout();

      showToast({
        type: "success",
        message: "Logout realizado com sucesso",
        duration: 3000,
      });
    } catch (error) {
      showToast({
        type: "success",
        message: "Sessão encerrada",
        duration: 3000,
      });
    } finally {
      setIsLoggingOut(false);
      window.location.href = "/";
    }
  };

  return (
    <Container>
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Perfil do Usuário" />
      </Box>
      <ContentContainer>
        <ImageContainer>
          <StyledAvatar>
            {account.picture ? (
              <img src={account.picture} alt={account.username} />
            ) : (
              <PersonIcon sx={{ width: 50, height: 50 }} />
            )}
          </StyledAvatar>
        </ImageContainer>

        <BoxWrapper>
          <SectionTitle>
            <PersonIcon fontSize="small" />
            Informações Pessoais
          </SectionTitle>
          <InfoText>
            <InfoLabel>{fallback(account.username)}</InfoLabel>
          </InfoText>
          <InfoText>
            <InfoLabel>CPF: {fallback(account.cpf)}</InfoLabel>
          </InfoText>
        </BoxWrapper>

        <BoxWrapper>
          <SectionTitle>
            <PhoneIcon fontSize="small" />
            Contato
          </SectionTitle>
          <InfoText>
            <InfoLabel>{fallback(account.telephone)}</InfoLabel>
          </InfoText>
        </BoxWrapper>

        <BoxWrapper>
          <SectionTitle>
            <BusinessIcon fontSize="small" />
            Empresa
          </SectionTitle>
          <InfoText>
            <InfoLabel>{fallback(account.company?.name)}</InfoLabel>
          </InfoText>
          <InfoText>
            <InfoLabel>CNPJ: {fallback(account.company?.cnpj)}</InfoLabel>
          </InfoText>
        </BoxWrapper>
      </ContentContainer>

      {/* Botão fixo no rodapé */}
      <Box
        sx={{
          p: 2,
          textAlign: "center",
          borderTop: "1px solid #f0f0f0",
          backgroundColor: "#fff",
          position: "sticky",
          bottom: 0,
          zIndex: 11,
          minHeight: "64px",
          maxHeight: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleLogout}
          disabled={isLoggingOut}
          startIcon={
            isLoggingOut ? <CircularProgress size={20} /> : <LogoutIcon />
          }
          fullWidth
          sx={{
            borderColor: "#d32f2f",
            color: "#d32f2f",
            "&:hover": {
              borderColor: "#b71c1c",
              backgroundColor: "#ffebee",
            },
            "&:disabled": {
              borderColor: "#cccccc",
              color: "#cccccc",
            },
            fontSize: "14px",
            padding: "8px",
            textTransform: "none",
            fontWeight: 500,
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "14px" }}>
            {isLoggingOut ? "Saindo..." : "Sair"}
          </Typography>
        </Button>
      </Box>
    </Container>
  );
};

export default UserProfile;
